import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/components/seo";
import { Reveal, SectionHead, Marquee } from "@/components/motion";

const IMG = "https://images.unsplash.com/photo-1769147339214-076740872485?crop=entropy&cs=srgb&fm=jpg&q=80";

export default function About() {
  const { t, pathFor } = useLang();
  useSEO("about");
  const a = t.aboutPage;

  return (
    <div className="bg-anthracite-dark pt-16" data-testid="about-page">
      <section className="border-b border-white/10 bg-anthracite grid-lines-dark">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28">
          <SectionHead kicker={a.kicker} title={a.title} light testid="about" />
        </div>
      </section>

      <Marquee items={t.marquee} />

      <section className="mx-auto grid max-w-7xl gap-16 px-4 py-20 sm:px-8 lg:grid-cols-[1fr_420px] lg:py-28">
        <div>
          {a.paragraphs.map((p, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div className="flex gap-6 border-t border-white/10 py-8 first:border-t-0 first:pt-0" data-testid={`about-paragraph-${i}`}>
                <span className="font-mono-tech text-sm text-royal-soft">0{i + 1}</span>
                <p className={`leading-relaxed ${i === 0 ? "text-lg text-white sm:text-xl" : "text-slate-400"}`}>{p}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="lg:sticky lg:top-28 lg:self-start">
          <div className="clip-frame overflow-hidden">
            <img src={IMG} alt="Präzisionsbauteil und technisches Getriebe in der Fertigung" className="h-80 w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-[480px]" loading="lazy" />
          </div>
          <p className="mt-4 font-mono-tech text-xs uppercase tracking-widest text-slate-500">Julian Rizzi — R-Mechanik SRLS, Gossensaß</p>
        </Reveal>
      </section>

      <section className="bg-slate-50 grid-lines-light" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:py-28">
          <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
            {a.values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06} className="h-full">
                <div className="group h-full bg-white p-8 transition-colors duration-300 hover:bg-anthracite" data-testid={`about-value-${i}`}>
                  <span className="font-mono-tech text-sm text-royal">0{i + 1}</span>
                  <h3 className="mt-4 text-lg font-black uppercase tracking-tight text-slate-900 transition-colors group-hover:text-white">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500 transition-colors group-hover:text-slate-300">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-anthracite">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-4 py-16 sm:px-8 lg:flex-row lg:items-center">
          <div>
            <h2 className="text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{a.ctaTitle}</h2>
            <p className="mt-3 max-w-xl text-slate-400">{a.ctaText}</p>
          </div>
          <Link to={pathFor("inquiry")} className="group flex shrink-0 items-center gap-3 bg-royal px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-royal-hover" data-testid="about-cta-button">
            {t.home.ctaButton}
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
